import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { AuthConfig, HealthResponse } from '@/lib/types'
import { X, CheckCircle, AlertCircle, Info, Save } from 'lucide-react'
import { RecentUpdates } from '@/components/RecentUpdates'
import updatesJson from '@/config/updates.json'

interface SettingsDrawerProps {
  open: boolean
  onClose: () => void
  authConfig: AuthConfig
  healthStatus: HealthResponse
  systemPrompt?: string
  onSystemPromptChange?: (prompt: string) => void
}

export function SettingsDrawer({ 
  open, 
  onClose, 
  authConfig, 
  healthStatus,
  systemPrompt,
  onSystemPromptChange,
}: SettingsDrawerProps) {
  const [editedPrompt, setEditedPrompt] = useState(systemPrompt || '')
  const [hasChanges, setHasChanges] = useState(false)
  const importedVersion = (updatesJson as any)?.currentVersion as string | undefined

  useEffect(() => {
    setEditedPrompt(systemPrompt || '')
    setHasChanges(false)
  }, [systemPrompt, open])

  // Version is resolved at build-time from updates.json; fallback is healthStatus.version

  const handlePromptChange = (value: string) => {
    setEditedPrompt(value)
    setHasChanges(value !== systemPrompt)
  }

  const handleSave = () => {
    if (onSystemPromptChange) {
      onSystemPromptChange(editedPrompt)
      setHasChanges(false)
    }
  }

  const handleReset = () => {
    setEditedPrompt('')
    if (onSystemPromptChange) {
      onSystemPromptChange('')
    }
    setHasChanges(false)
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-80 bg-background border-l shadow-lg">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <h2 className="text-lg font-semibold">Settings</h2>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-6">
            {/* System Status */}
            <div>
              <h3 className="text-sm font-medium mb-3">System Status</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  {healthStatus.status === 'ok' ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-red-500" />
                  )}
                  <span className="text-sm">Service Status</span>
                  <span className={`text-xs px-2 py-1 rounded ${
                    healthStatus.status === 'ok' 
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                  }`}>
                    {healthStatus.status}
                  </span>
                </div>
                
                <div className="flex items-center gap-2">
                  <Info className="w-4 h-4 text-blue-500" />
                  <span className="text-sm">Environment</span>
                  <span className="text-xs text-muted-foreground">
                    {healthStatus.environment}
                  </span>
                </div>
                
                <div className="flex items-center gap-2">
                  <Info className="w-4 h-4 text-blue-500" />
                  <span className="text-sm">Version</span>
                  <span className="text-xs text-muted-foreground">
                    {importedVersion || healthStatus.version}
                  </span>
                </div>
              </div>
            </div>

            {/* Authentication */}
            <div>
              <h3 className="text-sm font-medium mb-3">Authentication</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  {authConfig.auth_enabled ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-yellow-500" />
                  )}
                  <span className="text-sm">Azure AD</span>
                  <span className={`text-xs px-2 py-1 rounded ${
                    authConfig.auth_enabled 
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                  }`}>
                    {authConfig.auth_enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
                
                {authConfig.auth_enabled && (
                  <div className="text-xs text-muted-foreground">
                    <p>Tenant: {authConfig.aad_tenant_id ? `${authConfig.aad_tenant_id.slice(0, 8)}-****-****-****-****${authConfig.aad_tenant_id.slice(-4)}` : 'Not configured'}</p>
                    <p>Client: {authConfig.aad_client_id ? `${authConfig.aad_client_id.slice(0, 8)}-****-****-****-****${authConfig.aad_client_id.slice(-4)}` : 'Not configured'}</p>
                  </div>
                )}
              </div>
            </div>

            {/* System Prompt */}
            <div>
              <h3 className="text-sm font-medium mb-3">AI Assistant Behavior</h3>
              <div className="space-y-2">
                <label htmlFor="system-prompt" className="text-xs text-muted-foreground">
                  Custom System Prompt (leave empty for default)
                </label>
                <textarea
                  id="system-prompt"
                  value={editedPrompt}
                  onChange={(e) => handlePromptChange(e.target.value)}
                  placeholder="Enter custom instructions for the AI assistant..."
                  className="w-full h-32 px-3 py-2 text-sm border border-input bg-background rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                />
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    onClick={handleSave}
                    disabled={!hasChanges}
                    className="flex-1"
                  >
                    <Save className="w-3 h-3 mr-1" />
                    Save
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={handleReset}
                    className="flex-1"
                  >
                    Reset to Default
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground italic">
                  Changes apply to new conversations only
                </p>
              </div>
            </div>

            {/* Recent Updates */}
            <RecentUpdates />

            {/* About */}
            <div>
              <h3 className="text-sm font-medium mb-3">About</h3>
              <div className="text-xs text-muted-foreground space-y-1">
                <p>Giatec AI Assistant v{importedVersion || '1.3.0'}</p>
                <p>Powered by Azure OpenAI</p>
                <p>Built with React + FastAPI</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

